// Uclean-Admin-Dashboard/src/mocks/promo/promotions/handlers.js

import { http, HttpResponse } from 'msw';
import { BASE } from '../_shared/base';
import { paginate } from '../_shared/pagination';
import { forcePlatformOwner } from '../_shared/ownership';
import { engineError, validationError } from '../_shared/errors';
import { makeUuid } from '../_shared/ids';
import { promotionsStore, findPromotion } from './store';

const ROOT = `${BASE}/promotions`;

const TEMPLATES = ['welcome', 'reactivation', 'basket_value'];
const APP_MODES = ['auto', 'code'];

function validatePromoBody(body, { partial = false } = {}) {
  const errors = {};
  if (!partial || 'name' in body) {
    if (!body.name || typeof body.name !== 'string') {
      errors.name = ['The name field is required.'];
    }
  }
  if (!partial || 'template' in body) {
    if (!partial && !body.template) {
      errors.template = ['The template field is required.'];
    } else if (body.template && !TEMPLATES.includes(body.template)) {
      errors.template = [`The template must be one of: ${TEMPLATES.join(', ')}.`];
    }
  }
  if ('application_mode' in body && body.application_mode &&
      !APP_MODES.includes(body.application_mode)) {
    errors.application_mode = [`The application_mode must be one of: ${APP_MODES.join(', ')}.`];
  }
  return Object.keys(errors).length ? errors : null;
}

export const promotionsHandlers = [
  // GET list — supports page, per_page, status, template, group_id, q
  http.get(ROOT, ({ request }) => {
    const url = new URL(request.url);
    const status = url.searchParams.get('status');
    const template = url.searchParams.get('template');
    const groupId = url.searchParams.get('group_id');
    const q = (url.searchParams.get('q') || '').toLowerCase();

    let rows = promotionsStore.filter((p) => p.owner_type === 'platform');
    if (status) rows = rows.filter((p) => p.status === status);
    if (template) rows = rows.filter((p) => p.template === template);
    if (groupId) rows = rows.filter((p) => p.group_id === groupId);
    if (q) rows = rows.filter((p) => p.name.toLowerCase().includes(q));

    return HttpResponse.json(paginate(rows, {
      page: url.searchParams.get('page'),
      perPage: url.searchParams.get('per_page'),
    }));
  }),

  // GET one
  http.get(`${ROOT}/:id`, ({ params }) => {
    const found = findPromotion(params.id);
    if (!found) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json({ data: found });
  }),

  // POST — forces platform owner, validates shape
  http.post(ROOT, async ({ request }) => {
    const body = await request.json();
    const errs = validatePromoBody(body);
    if (errs) return validationError(errs);

    const owned = forcePlatformOwner(body);
    const row = {
      id: makeUuid(),
      current_redemptions: 0,
      budget_spent_minor: 0,
      priority: 100,
      stackable: true,
      ...owned,
      // Engine defaults new promotions to 'inactive' (admin activates
      // after rules are attached) — mirror it or tests drift again.
      status: owned.status ?? 'inactive',
    };
    promotionsStore.push(row);
    return HttpResponse.json({ data: row }, { status: 201 });
  }),

  // PATCH (full edit) — partial validation, owner stays forced platform
  http.patch(`${ROOT}/:id`, async ({ params, request }) => {
    const body = await request.json();
    const errs = validatePromoBody(body, { partial: true });
    if (errs) return validationError(errs);

    const idx = promotionsStore.findIndex((p) => p.id === params.id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const owned = forcePlatformOwner({ ...promotionsStore[idx], ...body });
    promotionsStore[idx] = owned;
    return HttpResponse.json({ data: promotionsStore[idx] });
  }),

  // PATCH /:id/status — toggle active↔inactive; reject reactivating exhausted
  http.patch(`${ROOT}/:id/status`, async ({ params, request }) => {
    const body = await request.json();
    const target = findPromotion(params.id);
    if (!target) return new HttpResponse(null, { status: 404 });

    if (body.status === 'active' && target.status === 'exhausted') {
      return engineError('exhausted', {
        status: 409,
        message: 'Promotion has reached its cap.',
      });
    }
    target.status = body.status;
    return HttpResponse.json({ data: target });
  }),

  // DELETE — soft delete (flip status to inactive)
  http.delete(`${ROOT}/:id`, ({ params }) => {
    const target = findPromotion(params.id);
    if (!target) return new HttpResponse(null, { status: 404 });
    target.status = 'inactive';
    return HttpResponse.json({ data: target });
  }),
];
